import React from 'react';

const CookiePolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: October 2, 2025</p>

          <div className="prose prose-lg max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-700">
                Cookies are small text files that are placed on your device (computer, smartphone, or other
                electronic device) when you visit a website. They are widely used to make websites work more
                efficiently and provide information to website owners.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. How We Use Cookies</h2>
              <p className="text-gray-700">
                FutureLens uses cookies and similar tracking technologies to enhance your experience on our
                platform. We use cookies to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Keep you signed in to your account</li>
                <li>Remember your preferences and settings</li>
                <li>Understand how you use our services</li>
                <li>Improve our platform's performance and functionality</li>
                <li>Provide personalized content and recommendations</li>
                <li>Analyze usage patterns and trends</li>
                <li>Protect against fraud and enhance security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Types of Cookies We Use</h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 Essential Cookies</h3>
              <p className="text-gray-700">
                These cookies are necessary for the website to function properly. They enable core functionality
                such as security, network management, and accessibility. You cannot opt out of these cookies.
              </p>
              <div className="bg-blue-50 rounded-lg p-4 mt-3">
                <p className="text-sm text-gray-700">
                  <strong>Examples:</strong> Authentication cookies, security cookies, session management
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 Performance Cookies</h3>
              <p className="text-gray-700">
                These cookies help us understand how visitors interact with our website by collecting and
                reporting information anonymously. This helps us improve how our website works.
              </p>
              <div className="bg-blue-50 rounded-lg p-4 mt-3">
                <p className="text-sm text-gray-700">
                  <strong>Examples:</strong> Analytics cookies, error reporting, performance monitoring
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.3 Functional Cookies</h3>
              <p className="text-gray-700">
                These cookies enable the website to provide enhanced functionality and personalization. They may
                be set by us or by third-party providers whose services we have added to our pages.
              </p>
              <div className="bg-blue-50 rounded-lg p-4 mt-3">
                <p className="text-sm text-gray-700">
                  <strong>Examples:</strong> Language preferences, user interface customization, remember me functionality
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.4 Targeting/Advertising Cookies</h3>
              <p className="text-gray-700">
                These cookies may be set through our site by our advertising partners. They may be used to build
                a profile of your interests and show you relevant advertisements on other sites.
              </p>
              <div className="bg-blue-50 rounded-lg p-4 mt-3">
                <p className="text-sm text-gray-700">
                  <strong>Examples:</strong> Ad tracking, remarketing, conversion tracking
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Third-Party Cookies</h2>
              <p className="text-gray-700">
                We may use third-party services that set cookies on your device. These third parties may include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Analytics Providers:</strong> To help us understand how users interact with our service</li>
                <li><strong>Authentication Services:</strong> To enable secure login functionality</li>
                <li><strong>Payment Processors:</strong> To process subscription payments securely</li>
                <li><strong>Cloud Services:</strong> To host and deliver our services</li>
              </ul>
              <p className="text-gray-700 mt-4">
                These third parties have their own privacy policies and cookie policies, which we encourage you
                to review.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Cookie Duration</h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.1 Session Cookies</h3>
              <p className="text-gray-700">
                These are temporary cookies that expire when you close your browser. They are used to maintain
                your session while you navigate through our website.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.2 Persistent Cookies</h3>
              <p className="text-gray-700">
                These cookies remain on your device for a set period or until you delete them. They help us
                recognize you when you return to our website and remember your preferences.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Managing Cookies</h2>
              <p className="text-gray-700">
                You have the right to decide whether to accept or reject cookies. You can exercise your cookie
                rights by setting your preferences in your browser settings.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.1 Browser Settings</h3>
              <p className="text-gray-700">
                Most web browsers allow you to control cookies through their settings. You can usually find these
                settings in the "options" or "preferences" menu of your browser. Here are links to cookie settings
                for popular browsers:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Google Chrome: Settings → Privacy and security → Cookies and other site data</li>
                <li>Mozilla Firefox: Settings → Privacy & Security → Cookies and Site Data</li>
                <li>Safari: Preferences → Privacy → Cookies and website data</li>
                <li>Microsoft Edge: Settings → Cookies and site permissions</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.2 Impact of Disabling Cookies</h3>
              <p className="text-gray-700">
                Please note that if you disable certain cookies, some features of our website may not function
                properly, and your user experience may be affected. Essential cookies cannot be disabled if you
                want to use our service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Do Not Track</h2>
              <p className="text-gray-700">
                Some browsers include a "Do Not Track" (DNT) feature that signals to websites you visit that you
                do not want to have your online activity tracked. Currently, there is no standard way for websites
                to respond to DNT signals, and we do not respond to DNT browser signals at this time.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Updates to This Cookie Policy</h2>
              <p className="text-gray-700">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for
                other operational, legal, or regulatory reasons. We encourage you to review this policy
                periodically to stay informed about how we use cookies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. More Information</h2>
              <p className="text-gray-700">
                For more information about how we process your personal data, please see our{' '}
                <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Contact Us</h2>
              <p className="text-gray-700">
                If you have questions about our use of cookies, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mt-4">
                <p className="text-gray-700">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:connect@optivusbusinesssolutions.com" className="text-primary hover:underline">
                    connect@optivusbusinesssolutions.com
                  </a>
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Phone:</strong>{' '}
                  <a href="tel:314-514-5125" className="text-primary hover:underline">
                    314-514-5125
                  </a>
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Address:</strong><br />
                  1200 Lake Saint Louis Blvd, Unit #325<br />
                  Lake Saint Louis, MO 63367
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
